import React from 'react';
import { Link } from 'react-router-dom';
import useSectionView from '../../hooks/useSectionView';
import { PRICING } from '../../constants/pricing';

const MONTHLY_BULLETS = [
  'Full access to all features',
  'Tax reserve, cash flow, income curves, IRS walkthrough',
  `Intro rate for your first ${PRICING.monthly.introMonths} months`,
  'Cancel anytime',
];

const ANNUAL_BULLETS = [
  'Pay in hail season, covered through winter',
  'Everything in monthly',
  `Save ~$${PRICING.monthly.intro * 12 - PRICING.annual.intro}/year vs monthly`,
  `Intro rate for your first year`,
  'Cancel anytime',
];

function PricingCard({ price, period, badge, bullets, highlighted, priceNote }) {
  return (
    <div style={{
      backgroundColor: 'var(--card)',
      border: highlighted ? '1.5px solid var(--signal)' : 'none',
      borderRadius: 'var(--r-card)',
      padding: '36px 32px',
      boxShadow: highlighted ? '0 8px 32px rgba(24, 95, 165, 0.12)' : 'var(--sh-card)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    }}>
      {highlighted && (
        <div style={{
          position: 'absolute',
          top: '-13px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'var(--signal)',
          color: 'var(--on-signal)',
          fontFamily: 'var(--font-sans)',
          fontSize: '12px',
          fontWeight: 500,
          padding: '4px 14px',
          borderRadius: '20px',
          whiteSpace: 'nowrap',
        }}>
          Best value
        </div>
      )}

      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: highlighted ? 'var(--signal-soft)' : 'var(--paper)',
        borderRadius: '20px',
        padding: '4px 12px',
        marginBottom: '20px',
        alignSelf: 'flex-start',
      }}>
        <span style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '12px',
          fontWeight: 500,
          color: highlighted ? 'var(--signal-ink)' : 'var(--slate)',
        }}>
          {badge}
        </span>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <span style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '48px',
          color: 'var(--ink)',
          lineHeight: 1,
        }}>
          {price}
        </span>
        <span style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '15px',
          color: 'var(--mute)',
          marginLeft: '6px',
        }}>
          {period}
        </span>
        {priceNote && (
          <p style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            color: 'var(--mute)',
            margin: '8px 0 0 0',
          }}>
            {priceNote}
          </p>
        )}
      </div>

      <ul style={{
        listStyle: 'none',
        padding: 0,
        margin: '0 0 32px 0',
        flexGrow: 1,
      }}>
        {bullets.map((b, i) => (
          <li key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            fontFamily: 'var(--font-sans)',
            fontSize: '14px',
            color: 'var(--slate)',
            lineHeight: 1.5,
            marginBottom: i < bullets.length - 1 ? '10px' : 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
              <circle cx="8" cy="8" r="7.25" style={{ stroke: 'var(--signal)' }} strokeWidth="1.5" />
              <polyline points="5,8 7,10 11,6" style={{ stroke: 'var(--signal)' }} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {b}
          </li>
        ))}
      </ul>

      <Link
        to="/signup"
        style={{
          // flex centring and border-box replace what the <button> supplied on
          // its own: form controls centre their label vertically and default to
          // border-box, an <a> does neither. Without box-sizing the outlined
          // card's 1.5px border would render it 51px tall and push it past 100%
          // of the card width. Every other value is unchanged.
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          textDecoration: 'none',
          height: '48px',
          backgroundColor: highlighted ? 'var(--signal)' : 'var(--on-signal)',
          color: highlighted ? 'var(--on-signal)' : 'var(--signal)',
          border: highlighted ? 'none' : '1.5px solid var(--signal)',
          borderRadius: '8px',
          padding: '0 24px',
          fontFamily: 'var(--font-sans)',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
          width: '100%',
          transition: 'background-color 0.15s, color 0.15s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = highlighted ? 'var(--signal-ink)' : 'var(--signal-soft)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = highlighted ? 'var(--signal)' : 'var(--card)';
        }}
      >
        Get started
      </Link>
    </div>
  );
}

function Pricing() {
  const sectionRef = useSectionView('section_viewed_pricing');
  return (
    <section ref={sectionRef} style={{ backgroundColor: 'var(--card)', padding: '96px 24px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h2 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '36px',
          color: 'var(--ink)',
          textAlign: 'center',
          margin: '0 0 8px 0',
        }}>
          Simple, honest pricing
        </h2>
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '15px',
          color: 'var(--mute)',
          textAlign: 'center',
          margin: '0 0 48px 0',
        }}>
          Intro rate for your first year, then standard pricing. Cancel anytime.
        </p>

        <div className="pr-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '24px',
          alignItems: 'start',
        }}>
          <PricingCard
            price={`$${PRICING.monthly.intro}`}
            period="/month"
            badge={`Intro rate for ${PRICING.monthly.introMonths} months`}
            bullets={MONTHLY_BULLETS}
            highlighted={false}
            priceNote={`$${PRICING.monthly.base}/mo after your first ${PRICING.monthly.introMonths} months`}
          />
          <PricingCard
            price={`$${PRICING.annual.intro}`}
            period="/year"
            badge="Intro rate for 1 year"
            bullets={ANNUAL_BULLETS}
            highlighted={true}
            priceNote={`$${PRICING.annual.base}/yr after your first year`}
          />
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .pr-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

export default Pricing;
